import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/containers/Main'
import { Button, Form, Row, Tooltip, OverlayTrigger, InputGroup } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddUser from './resolvers/resAddUser'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import useDynamicFilter from 'hooks/useDynamicFilter'
import * as Icon from 'react-bootstrap-icons'
import SelectWrapper from 'shared/components/SelectWrapper'
import { togglePasswordInput, generateRandomPassword } from 'utils/passwordUtils'
import { statuses } from 'consts'
import { getCustomData } from 'shared/components/App/Auth'
import getBrandContent from 'utils/getBrandContent'
import BackButton from 'shared/components/BackButton'
import Footer from 'shared/components/Footer'
import capitalize from 'utils/capitalize'

const submitRef = React.createRef<HTMLButtonElement>()

const AddUser: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)

  const [states, setStates] = useState<any>([])
  const [allStates, setAllStates] = useState<any>(false)
  const [countries, setCountries] = useState<any>([])
  const [currentCountry, setCurrentCountry] = useState<any>(null)

  const [passwordInputType, setPasswordInputType] = useState<string>('password')

  const { id } = useParams()

  const isRoleAndStatusInputDisabled: boolean = !(id !== getCustomData('userId')?.toString())

  const navigate = useNavigate()

  const {
    control,
    register,
    getValues,
    handleSubmit,
    reset,
    setFocus,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: resAddUser,
  })

  useInit({
    getValues,
    id,
    reset,
    setDataLoaded,
    setShowOverlay,
    setStates,
    setAllStates,
    setCountries,
    setCurrentCountry,
    setValue,
  })

  useFocusError({
    errors,
    setFocus,
  })

  useDynamicFilter({
    allRows: allStates,
    setFilteredRows: setStates,
    filterColumnName: 'country_id',
    inputValue: currentCountry,
    setDefaultFormFieldValue: setValue,
    formFieldValueName: 'state',
    isFormFieldValueNameObject: true,
    currentFormValues: getValues(),
    identityColumnName: 'id',
  })

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      showCardHeader={true}
      showMinimizedBarButton={true}
      headerInsert={<BackButton url='/superadmins' text='All Superadmins' />}
      actionInsert={
        <Button
          disabled={!dataLoaded}
          type='submit'
          name='save'
          className='btn-round btn-primary'
          onClick={() => {
            if (submitRef.current != null) {
              submitRef.current.click()
            }
          }}
        >
          Save Superadmin
        </Button>
      }
    >
      <Form
        onSubmit={handleSubmit((data) =>
          prepareSubmitForm({
            submitForm,
            navigate,
            data,
            setShowOverlay,
            id,
            name: 'Superadmin',
            setError: setError,
            successNav: '/superadmins',
          }),
        )}
        id='main-form'
      >
        <InputWrapper label='Status' name='status' errors={errors}>
          <Form.Select {...register('status')} disabled={isRoleAndStatusInputDisabled}>
            <option key={0} value={statuses.pending}>
              {capitalize(statuses.pending)}
            </option>

            <option key={1} value={statuses.active}>
              {capitalize(statuses.active)}
            </option>
          </Form.Select>
        </InputWrapper>
        <InputWrapper label='First Name' name='firstname' errors={errors}>
          <Form.Control type='text' {...register('firstname')} />
        </InputWrapper>
        <InputWrapper label='Last Name' name='lastname' errors={errors}>
          <Form.Control type='text' {...register('lastname')} />
        </InputWrapper>
        {!id && (
          <InputWrapper label='Password' name='password_api' errors={errors}>
            <InputGroup className='mb-3'>
              <Form.Control type={passwordInputType} {...register('password_api')} />
              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>Generate Password</Tooltip>}
              >
                <Button
                  variant='outline-primary'
                  onClick={() => setValue('password_api', generateRandomPassword())}
                >
                  <Icon.Key title='Generate random password' />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement='top'
                delay={{ show: 250, hide: 400 }}
                overlay={
                  <Tooltip>
                    {passwordInputType === 'password' ? 'Show password' : 'Hide password'}
                  </Tooltip>
                }
              >
                <Button
                  variant='outline-warning'
                  onClick={() => setPasswordInputType(togglePasswordInput(passwordInputType))}
                >
                  {passwordInputType === 'password' ? <Icon.Unlock /> : <Icon.Lock />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </InputWrapper>
        )}
        <InputWrapper label='Email' name='email' errors={errors}>
          <Form.Control type='email' {...register('email')} />
        </InputWrapper>
        <InputWrapper label='Phone' name='phone' errors={errors}>
          <Form.Control type='phone' {...register('phone')} />
        </InputWrapper>
        <InputWrapper label='City' name='city' errors={errors}>
          <Form.Control type='city' {...register('city')} />
        </InputWrapper>
        <InputWrapper label='Country' name='country' errors={errors}>
          <SelectWrapper
            name='country'
            control={control}
            options={countries}
            optionLabel='name'
            optionValue='id'
            placeholder='Select Country...'
            onChange={(option) => setCurrentCountry(option?.id)}
          />
        </InputWrapper>
        <InputWrapper label='State / Province' name='state_id' errors={errors}>
          <SelectWrapper
            name='state'
            control={control}
            options={states}
            optionLabel='name'
            optionValue='id'
            placeholder='Select State...'
          />
        </InputWrapper>
        <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>
      </Form>
    </Layout>
  )
}

export default AddUser
